import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { dailyCheckinApi } from "../api/dailyCheckin";
import { protocolInputsApi } from "../api/protocolInputs";
import { protocolsApi } from "../api/protocols";
import { ALIGNMENT_METRICS } from "../components/alignmentMetrics";
import BreathingGuide from "../components/BreathingGuide";
import { BottomNav } from "../components/BottomNav";
import { EVENING_PROTOCOL_ID } from "../utils/activeProtocol";

// Evening Wind-Down — the home-screen evening ritual.
//
// A self-contained, forced-linear flow (one step per screen, same pattern
// as the Morning Check-in — no checklist view). Launch screen, then four
// screens — five on nights the variable slot rendered a rotation step —
// tuned to complete in under three minutes (F&F feedback: the six-step
// version felt like a chore):
//
//   1. Breathing Reset      → the animated breathing guide (the F&F
//                             "chefs kiss" moment — kept full-size, moved
//                             first so the ritual opens by settling)
//   2. How Today Felt       → the 3-question felt-alignment module;
//                             upserts daily_self_report (feeds the Values
//                             Alignment Index / PSYCH — required RCI input)
//   3. Tonight's rotation   → (rotation nights only) the day's variable
//                             library step from /today — gratitude stack,
//                             worry offload, highlight reel, … — walked
//                             and completed like any fixed step; authored
//                             rotations write their protocol_inputs row
//   4. Intention Set        → protocol_inputs (evening_intention) —
//                             resurfaced by the morning check-in's
//                             "Yesterday you said" and the midday reset.
//                             Doubles as Tomorrow's Anchor: the old
//                             separate anchor step asked the same
//                             "what about tomorrow" question, so the two
//                             are one step
//   5. Screen Cutoff        → protocol_inputs (screen_commitment) — the
//                             morning "Screens last night" follow-through
//                             gate reads this exact step_type. Six one-tap
//                             time chips + a Custom chip (inline manual
//                             time entry) + the DND toggle (July 14 2026 —
//                             restored over the July 12 single-statement
//                             tap, with a Custom option added). The chosen
//                             time is informational only — never enforced.
//                             Unlike the pre-July-12 picker, submit ALWAYS
//                             writes a screen_commitment row (no
//                             empty-commitment gap); the row's created_at
//                             is the precise timestamp the follow-through
//                             gate reads. Final step closes to Home
//
// This guided flow is the SINGLE surface for the Evening Wind-Down
// protocol. The old single-page checklist (Protocol.jsx) is retired for
// it — the Protocol tab and any /protocol/evening-wind-down link redirect
// here (July 2026). The completion contract: the flow records a completion
// for every step it walks the user through, and ONLY those — nothing is
// back-filled. (The old checklist-era back-fill marked steps the user
// never performed as done, both visually and as protocol_completions rows
// feeding the BEH signal — F&F: "it checked off boxes I didn't do." The
// rotation step returned July 2026 as a real screen INSIDE the ritual,
// so walking the flow completes the full rendered set honestly.)

// User-local YYYY-MM-DD — matches the local-date contract used across the
// app (CheckIn.jsx / Protocol.jsx) so writes bucket against the user's
// calendar day, not UTC.
function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// localStorage marker: "evening ritual completed today". Lets the Home
// screen reflect the completed state without a network round-trip.
const EVENING_DONE_KEY = "haiven.evening.v1";
function markEveningComplete() {
  try { localStorage.setItem(EVENING_DONE_KEY, todayKey()); } catch { /* private mode */ }
}
// Same marker, read side — the launch screen swaps to "Revisit" copy when
// tonight's ritual is already complete (mirrors the Dashboard card, which
// reads this key via its own eveningDoneToday()). Re-running the flow is
// harmless: completions are idempotent per (user, step, day).
function eveningDoneTonight() {
  try { return localStorage.getItem(EVENING_DONE_KEY) === todayKey(); } catch { return false; }
}

// Protocol + fixed step ids (see app/data/protocols.py). Step ids are
// STABLE; only their ``order`` changes across reorders. The flow completes
// a step inline as the user advances past it; steps it never shows (Body
// Scan, the day's variable library step) are intentionally left unrecorded
// — see the header note on why the old back-fill was removed.
const PROTOCOL = EVENING_PROTOCOL_ID;
const STEP = {
  intention: "step-5",
  breathing: "step-4",
  screenCutoff: "step-1",
};

// The day's rotating library step (from /today's variable slot) renders as
// its own screen between How Today Felt and Intention, and is completed
// inline like every other walked step. Three rotation steps capture text;
// this maps their library ids to the protocol_inputs step_type each one
// writes (the same types the old checklist used, so downstream reads are
// unchanged). Unmapped rotation steps (gratitude stack, PMR, …) are
// do-it-now screens with no textarea.
const ROTATION_INPUTS = {
  "tomorrow-priority": {
    stepType: "tomorrow_priority",
    label: "Write the one thing tomorrow needs",
    placeholder: "One thing. Be specific.",
  },
  "worry-offload": {
    stepType: "worry_offload",
    label: "Write what you're carrying",
    placeholder: "Write what you're carrying. Don't solve it. Just get it out.",
  },
  "highlight-reel": {
    stepType: "today_highlight",
    label: "Write the moment worth keeping",
    placeholder: "One moment from today...",
  },
};

// Screen Cutoff quick-select times — one tap instead of typing. The chip
// label IS the commitment text the morning follow-through resurfaces
// ("Yesterday you said: 10:30pm"), so keep them human-readable. A typed
// Custom time writes the SAME input_text field in the same string shape
// ("10:47pm") — one field, one format, chips or custom.
const CUTOFF_TIMES = ["9:00pm", "9:30pm", "10:00pm", "10:30pm", "11:00pm", "11:30pm"];

// Written when the user submits without picking a chip, typing a custom
// time, or flipping the DND toggle — the row must still land (the
// morning follow-through gate reads the row's existence via created_at;
// the pre-July-12 picker skipped the write entirely in this case).
const NO_TIME_COMMITMENT = "No time selected";

const WHY_COPY =
  "How you close the day shapes how easily you settle into rest. A " +
  "consistent wind-down helps you unwind and sleep more soundly.";

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .evening { font-family: 'DM Sans', sans-serif; background: ${P.bg};
    min-height: 100vh; color: ${P.text}; max-width: 420px; margin: 0 auto; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  .step-enter { animation: fadeUp 0.42s ease forwards; }
  .ev-btn-primary {
    width: 100%; padding: 17px; background: ${P.dark};
    border: none; border-radius: 16px; cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px; font-style: italic; color: ${P.bg};
    transition: opacity 0.15s; letter-spacing: 0.01em;
  }
  .ev-btn-primary:hover { opacity: 0.85; }
  .ev-btn-primary:disabled { opacity: 0.6; cursor: default; }
  .ev-textarea {
    width: 100%; background: ${P.surface}; border: 1.5px solid ${P.border};
    border-radius: 16px; padding: 18px; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 300; color: ${P.text}; line-height: 1.75;
    resize: none; outline: none; transition: border-color 0.2s;
  }
  .ev-textarea:focus { border-color: ${P.primaryMid}; }
  .ev-textarea::placeholder { color: ${P.textFaint}; font-style: italic; }
`;

// Top chrome shared by every step: back chevron (left), "X of N" step
// indicator (center; N is 5 on nights a rotation step rendered, else 4).
// Matches the Morning Check-in pattern.
function StepBar({ index, total, onBack }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0",
    }}>
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          width: 40, height: 40, borderRadius: "50%", border: "none",
          background: "transparent", cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center", marginLeft: -8,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={P.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </button>
      <div style={{
        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
        color: P.textMute, fontWeight: 500, fontVariantNumeric: "tabular-nums",
      }}>
        {index} of {total}
      </div>
      <div style={{ width: 40 }} />
    </div>
  );
}

// Optional, collapsed explainer — preserves the "why" without forcing it
// into the minimal launch screen.
function WhyExpand() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 28, textAlign: "center" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
          color: P.primary, fontWeight: 500, padding: 8,
        }}
      >
        {open ? "Hide" : "Why close the day this way"}
      </button>
      {open && (
        <p style={{
          marginTop: 10, fontSize: 13, color: P.textMid, fontWeight: 300,
          lineHeight: 1.7, maxWidth: 320, marginLeft: "auto", marginRight: "auto",
        }}>
          {WHY_COPY}
        </p>
      )}
    </div>
  );
}

// One felt-alignment metric, rendered full-screen as a row of 1–5 buttons.
function MetricSelector({ metric, value, onSelect }) {
  return (
    <div role="group" aria-label={metric.question} style={{ marginBottom: 26 }}>
      <div style={{
        fontSize: 10, color: P.primary, letterSpacing: "0.16em",
        textTransform: "uppercase", fontWeight: 600, marginBottom: 8,
      }}>
        {metric.kicker}
      </div>
      <p style={{
        fontSize: 14, color: P.textMid, fontWeight: 300, lineHeight: 1.5,
        marginBottom: 12, fontStyle: "italic",
      }}>
        {metric.question}
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              aria-label={`${metric.kicker}: ${n} of 5`}
              aria-pressed={selected}
              onClick={() => onSelect(selected ? null : n)}
              style={{
                flex: 1, height: 52, borderRadius: 14, cursor: "pointer",
                border: `1.5px solid ${selected ? P.primary : P.border}`,
                background: selected ? P.primary : P.surface,
                color: selected ? P.bg : P.textMid,
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 19, fontStyle: "italic", lineHeight: 1,
                transition: "all 0.15s",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <span style={{ fontSize: 11, color: P.textFaint }}>{metric.low} · 1</span>
        <span style={{ fontSize: 11, color: P.textFaint }}>{metric.high} · 5</span>
      </div>
    </div>
  );
}

// Step header — kicker + serif title + optional supporting copy. Shared by
// every numbered step so the four screens read as one cohesive sequence.
function StepHead({ kicker, title, body }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        fontSize: 10, color: P.primary, letterSpacing: "0.18em",
        textTransform: "uppercase", fontWeight: 600, marginBottom: 14,
      }}>
        {kicker}
      </div>
      <h1 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 30, fontStyle: "italic", fontWeight: 400,
        color: P.text, lineHeight: 1.2, marginBottom: body ? 14 : 0,
      }}>
        {title}
      </h1>
      {body && (
        <p style={{ fontSize: 14, color: P.textMid, fontWeight: 300, lineHeight: 1.7, maxWidth: 330 }}>
          {body}
        </p>
      )}
    </div>
  );
}

export default function EveningWindDown() {
  const navigate = useNavigate();
  const { token } = useAuth();

  // 0 = launch screen; 1..4 = the four linear steps.
  const [step, setStep] = useState(0);
  const [intentionText, setIntentionText] = useState("");
  const [report, setReport] = useState({
    alignment_1_5: null,
    regulation_1_5: null,
    presence_1_5: null,
  });
  // Screen Cutoff state — chip time, typed custom time, and the DND
  // toggle. Only one time value is active at once: picking a chip clears
  // the custom entry and vice versa (see the handlers on the screen).
  const [screenTime, setScreenTime] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState("");
  const [screensOff, setScreensOff] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [saving, setSaving] = useState(false);
  // Server-truth "tonight is already done". The localStorage marker
  // (eveningDoneTonight) is device-local — a second device or a cleared
  // storage read "Start your evening reflection" for a ritual the server
  // already has all three completions for. Best-effort: on failure we
  // just fall back to the local marker.
  //
  // The same /today fetch also carries tonight's rotating library step
  // (the variable slot the backend injects — gratitude stack, worry
  // offload, highlight reel, …). It gets its own screen in the flow.
  const [serverDone, setServerDone] = useState(false);
  const [fetchedRotation, setFetchedRotation] = useState(null);
  // Frozen copy of the rotation at the moment the user taps Start. The
  // fetch is async; if it resolved mid-ritual, the screen numbering
  // would shift under the user (step 3 flipping from Intention to the
  // rotation while they type). Freezing at launch means a slow fetch
  // simply gives a 4-screen night — honest, and it self-heals on a
  // revisit because completions are idempotent per (user, step, day).
  const [rotation, setRotation] = useState(null);
  const [rotationText, setRotationText] = useState("");
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    Promise.all([
      protocolsApi.completions(PROTOCOL, todayKey(), token)
        .then((d) => (d.completions || []).length),
      protocolsApi.today(PROTOCOL, todayKey(), token)
        .then((detail) => detail?.steps || []),
    ])
      .then(([done, steps]) => {
        if (cancelled) return;
        if (steps.length > 0 && done >= steps.length) setServerDone(true);
        setFetchedRotation(steps.find((s) => s.variable) || null);
      })
      .catch(() => { /* fall back to the local marker; no rotation tonight */ });
    return () => { cancelled = true; };
  }, [token]);
  const doneTonight = eveningDoneTonight() || serverDone;

  // Screen numbering for tonight's walk. With a rotation: Breathing (1) →
  // How Today Felt (2) → rotation (3) → Intention (4) → Screen Cutoff (5).
  // Without: the original four screens.
  const hasRotation = !!rotation;
  const rotationIdx = 3;
  const intentionIdx = hasRotation ? 4 : 3;
  const cutoffIdx = hasRotation ? 5 : 4;
  const totalScreens = hasRotation ? 5 : 4;
  const rotationInput = hasRotation ? ROTATION_INPUTS[rotation.id] : null;

  // ── Best-effort writes ─────────────────────────────────────────────
  // Every write is fire-and-forget and never blocks advancing — a cold
  // backend or a flaky network must not trap the user mid-ritual. Mirrors
  // the resilience posture of CheckIn.jsx / Protocol.jsx.
  const completeStep = (stepId) => {
    if (!token) return Promise.resolve();
    return protocolsApi
      .complete({ protocol_id: PROTOCOL, step_id: stepId, date: todayKey() }, token)
      .catch(() => {});
  };
  const writeInput = (stepId, stepType, text) => {
    const t = (text || "").trim();
    if (!t || !token) return;
    protocolInputsApi
      .create(
        { protocol_id: PROTOCOL, step_id: stepId, step_type: stepType, input_text: t, date: todayKey() },
        token,
      )
      .catch(() => {});
  };

  const go = (n) => setStep(n);

  // Step 1 — Breathing Reset (the animated guide; completes inline)
  const submitBreathing = () => {
    completeStep(STEP.breathing);
    go(2);
  };

  // Step 2 — How Today Felt → daily_self_report (only when answered).
  // Leaves fragmentation_1_5 / avoided_tension null per the brief.
  const submitReflection = async () => {
    const hasAny = Object.values(report).some((v) => v != null);
    setSaving(true);
    try {
      if (hasAny && token) {
        await dailyCheckinApi.selfReport({ date: todayKey(), ...report }, token);
      }
    } catch {
      /* non-blocking — the day's self-report is optional */
    } finally {
      setSaving(false);
      go(3);
    }
  };

  // Rotation screen (only on nights the variable slot rendered) — the
  // day's rotating library step, walked and completed like every fixed
  // step. Authored rotations (see ROTATION_INPUTS) also write their
  // protocol_inputs row when text was entered; the write is optional —
  // doing the step is the completion, the text is the capture.
  const submitRotation = () => {
    if (rotation) {
      if (rotationInput) {
        writeInput(rotation.id, rotationInput.stepType, rotationText);
      }
      completeStep(rotation.id);
    }
    go(intentionIdx);
  };

  // Intention Set → protocol_inputs (evening_intention).
  // The single forward-looking prompt (it doubles as Tomorrow's Anchor —
  // same question). evening_intention is the load-bearing step_type: the
  // morning check-in's "Yesterday you said" and the midday clarity reset
  // both resurface it.
  const submitIntention = () => {
    writeInput(STEP.intention, "evening_intention", intentionText);
    completeStep(STEP.intention);
    go(cutoffIdx);
  };

  // Screen Cutoff (final). Writes screen_commitment (the morning
  // "Screens last night" gate reads this step_type) — the chip label or
  // typed custom time ("10:30pm" / "10:47pm"), "Screens off" when only
  // the DND toggle is on, or NO_TIME_COMMITMENT when nothing was chosen.
  // The write ALWAYS lands (July 14 2026 — kept from the one-tap
  // version; the old picker skipped it when empty and the follow-through
  // gate lost the night). The chosen time is informational only — no
  // enforcement. Completes the step, then closes to Home.
  //
  // Every step recorded tonight was walked on screen — Breathing,
  // Intention, and the rotation (when rendered) completed inline as the
  // user advanced; Screen Cutoff completes here. Nothing is back-filled:
  // recording steps the user never performed inflated the BEH signal and
  // read as a glitch on the old checklist. See the header note.
  const finishEvening = async () => {
    if (committed) return; // double-tap guard
    setCommitted(true);
    const commitment =
      customText.trim() || screenTime || (screensOff ? "Screens off" : NO_TIME_COMMITMENT);
    writeInput(STEP.screenCutoff, "screen_commitment", commitment);
    await completeStep(STEP.screenCutoff);
    markEveningComplete();
    navigate("/dashboard");
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="evening">
        {/* Warm wash, mirrors the other screens */}
        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 340, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.09) 0%, transparent 68%)",
        }} />

        {/* Extra bottom padding on the launch step clears the tab bar. */}
        <div style={{ position: "relative", zIndex: 1, paddingBottom: step === 0 ? 110 : 48 }}>

          {/* ── Launch ──────────────────────────────────────────────── */}
          {step === 0 && (
            <div className="step-enter">
              <div style={{ padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0" }}>
                <button
                  onClick={() => navigate("/dashboard")}
                  aria-label="Back"
                  style={{
                    width: 40, height: 40, borderRadius: "50%", border: "none",
                    background: "transparent", cursor: "pointer", display: "flex",
                    alignItems: "center", justifyContent: "center", marginLeft: -8,
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke={P.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 6l-6 6 6 6" />
                  </svg>
                </button>
              </div>
              <div style={{
                padding: "20px 24px 0", minHeight: "64vh",
                display: "flex", flexDirection: "column", justifyContent: "center",
                textAlign: "center",
              }}>
                <div style={{
                  fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
                  color: P.primary, fontWeight: 600, marginBottom: 16,
                }}>
                  Evening
                </div>
                <h1 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 38, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.15, marginBottom: 14,
                }}>
                  Evening Reflection
                </h1>
                <p style={{
                  fontSize: 15, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.6, maxWidth: 280, margin: "0 auto 40px",
                }}>
                  {doneTonight
                    ? "Tonight's reflection is complete. Rest well — or walk it again."
                    : `Close the day with intention. ${fetchedRotation ? "Five" : "Four"} steps, under three minutes.`}
                </p>
                <button
                  className="ev-btn-primary"
                  onClick={() => {
                    // Freeze tonight's rotation at launch so a late fetch
                    // can't renumber the screens mid-ritual.
                    setRotation(fetchedRotation);
                    go(1);
                  }}
                >
                  {doneTonight ? "Revisit your evening" : "Start your evening reflection"}
                </button>
                <WhyExpand />
              </div>
            </div>
          )}

          {/* ── 1. Breathing Reset ──────────────────────────────────── */}
          {step === 1 && (
            <div className="step-enter">
              <StepBar index={1} total={totalScreens} onBack={() => go(0)} />
              <div style={{ padding: "26px 24px 0" }}>
                <StepHead
                  kicker="Breathing reset"
                  title="Settle your system."
                  body="Follow the circle — in for four, out for six. One round is the reset."
                />
                <BreathingGuide once />
                <div style={{ marginTop: 30 }}>
                  <button className="ev-btn-primary" onClick={submitBreathing}>
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── 2. How Today Felt ───────────────────────────────────── */}
          {step === 2 && (
            <div className="step-enter">
              <StepBar index={2} total={totalScreens} onBack={() => go(1)} />
              <div style={{ padding: "26px 24px 0" }}>
                <h1 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 30, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.2, marginBottom: 12,
                }}>
                  How today felt
                </h1>
                <p style={{
                  fontSize: 13.5, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.6, marginBottom: 32, maxWidth: 320,
                }}>
                  Your own read of the day — shapes how Haiven tracks your
                  alignment over time.
                </p>

                {ALIGNMENT_METRICS.map((metric) => (
                  <MetricSelector
                    key={metric.key}
                    metric={metric}
                    value={report[metric.key] ?? null}
                    onSelect={(n) => setReport((prev) => ({ ...prev, [metric.key]: n }))}
                  />
                ))}

                <div style={{ marginTop: 12 }}>
                  <button className="ev-btn-primary" onClick={submitReflection} disabled={saving}>
                    {saving ? "Saving…" : "These look right"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── 3 (rotation nights only). Tonight's rotating library
              step — its own screen, completed inline like every walked
              step. Authored rotations get an optional textarea; physical
              ones are do-it-now screens. ─────────────────────────────── */}
          {step === rotationIdx && hasRotation && (
            <div className="step-enter">
              <StepBar index={rotationIdx} total={totalScreens} onBack={() => go(2)} />
              <div style={{ padding: "26px 24px 0" }}>
                <StepHead
                  kicker={rotation.seen_before ? "Tonight's rotation" : "New tonight"}
                  title={rotation.title}
                  body={rotation.description}
                />
                {rotationInput && (
                  <>
                    <div style={{
                      fontSize: 10, color: P.primary, letterSpacing: "0.16em",
                      textTransform: "uppercase", fontWeight: 600, marginBottom: 8,
                    }}>
                      {rotationInput.label}
                    </div>
                    <textarea
                      className="ev-textarea"
                      rows={4}
                      placeholder={rotationInput.placeholder}
                      value={rotationText}
                      onChange={(e) => setRotationText(e.target.value)}
                      style={{ marginBottom: 20 }}
                    />
                  </>
                )}
                <button className="ev-btn-primary" onClick={submitRotation}>
                  {rotationInput ? "Continue" : "Done"}
                </button>
              </div>
            </div>
          )}

          {/* ── Intention Set ─────────────────────────────── */}
          {step === intentionIdx && (
            <div className="step-enter">
              <StepBar index={intentionIdx} total={totalScreens} onBack={() => go(hasRotation ? rotationIdx : 2)} />
              <div style={{ padding: "26px 24px 0" }}>
                <StepHead
                  kicker="Intention set"
                  title={<>What does tomorrow<br />need from you?</>}
                  body="One sentence — your anchor for tomorrow. Haiven surfaces it for you in the morning."
                />
                <textarea
                  className="ev-textarea"
                  rows={5}
                  placeholder="Tomorrow I intend to…"
                  value={intentionText}
                  onChange={(e) => setIntentionText(e.target.value)}
                  style={{ marginBottom: 20 }}
                />
                <button className="ev-btn-primary" onClick={submitIntention}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ── Screen Cutoff (final) ───────────────────────────────── */}
          {step === cutoffIdx && (
            <div className="step-enter">
              <StepBar index={cutoffIdx} total={totalScreens} onBack={() => go(intentionIdx)} />
              <div style={{ padding: "26px 24px 0" }}>
                <StepHead
                  kicker="Screen cutoff"
                  title="Put the phone down."
                  body="Set a commitment now — even 15 minutes before bed helps you wind down."
                />

                <div style={{
                  fontSize: 10, color: P.primary, letterSpacing: "0.16em",
                  textTransform: "uppercase", fontWeight: 600, marginBottom: 8,
                }}>
                  Screens off by
                </div>
                {/* One-tap time chips — no typing. Tap again to clear.
                    Picking a chip clears any typed custom time; only one
                    value is active at a time. */}
                <div
                  role="group"
                  aria-label="Screens off by"
                  style={{
                    display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 8, marginBottom: customOpen ? 12 : 18,
                  }}
                >
                  {CUTOFF_TIMES.map((t) => {
                    const selected = screenTime === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => {
                          setScreenTime(selected ? "" : t);
                          setCustomText("");
                          setCustomOpen(false);
                        }}
                        style={{
                          height: 48, borderRadius: 14, cursor: "pointer",
                          border: `1.5px solid ${selected ? P.primary : P.border}`,
                          background: selected ? P.primary : P.surface,
                          color: selected ? P.bg : P.textMid,
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 14, fontWeight: selected ? 500 : 300,
                          transition: "all 0.15s",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                  {/* Custom — reveals an inline manual time entry. Toggling
                      it on clears any chip; toggling it off clears the
                      typed value (tap-to-clear parity with the chips). */}
                  <button
                    type="button"
                    aria-pressed={customOpen}
                    onClick={() => {
                      if (customOpen) {
                        setCustomOpen(false);
                        setCustomText("");
                      } else {
                        setCustomOpen(true);
                        setScreenTime("");
                      }
                    }}
                    style={{
                      height: 48, borderRadius: 14, cursor: "pointer",
                      gridColumn: "1 / -1",
                      border: `1.5px solid ${customOpen ? P.primary : P.border}`,
                      background: customOpen ? P.primary : P.surface,
                      color: customOpen ? P.bg : P.textMid,
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 14, fontWeight: customOpen ? 500 : 300,
                      transition: "all 0.15s",
                    }}
                  >
                    Custom
                  </button>
                </div>

                {/* Inline manual time entry — writes the SAME
                    screen_commitment input_text the chips use, in the same
                    human-readable shape ("10:47pm"). */}
                {customOpen && (
                  <input
                    type="text"
                    aria-label="Custom screens-off time"
                    placeholder="e.g. 10:47pm"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    autoFocus
                    style={{
                      width: "100%", background: P.surface,
                      border: `1.5px solid ${P.primaryMid}`,
                      borderRadius: 14, padding: "14px 16px",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 14, fontWeight: 300, color: P.text,
                      outline: "none", marginBottom: 18,
                    }}
                  />
                )}

                {/* Screens-off toggle */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={screensOff}
                  onClick={() => setScreensOff((v) => !v)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 12,
                    background: P.surface, border: `1.5px solid ${screensOff ? P.primary : P.border}`,
                    borderRadius: 14, padding: "15px 16px", cursor: "pointer",
                    marginBottom: 26, transition: "border-color 0.15s",
                  }}
                >
                  <span style={{ fontSize: 14, color: P.textMid, fontWeight: 300, textAlign: "left" }}>
                    Phone on Do Not Disturb, out of reach
                  </span>
                  <span style={{
                    width: 44, height: 26, borderRadius: 99, flexShrink: 0,
                    background: screensOff ? P.primary : P.surfaceAlt,
                    position: "relative", transition: "background 0.15s",
                  }}>
                    <span style={{
                      position: "absolute", top: 3, left: screensOff ? 21 : 3,
                      width: 20, height: 20, borderRadius: "50%", background: "#F2EDE3",
                      transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }} />
                  </span>
                </button>

                <button
                  className="ev-btn-primary"
                  onClick={finishEvening}
                  disabled={committed}
                >
                  Complete your evening
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Tab bar on the launch screen only. The Protocol tab redirects
            here in the evening; without the nav that redirect stranded
            users in a chrome-less flow (back chevron to Home was the
            only exit). Once the ritual starts (step ≥ 1) the screen goes
            focused/chrome-less on purpose. */}
        {step === 0 && <BottomNav />}
      </div>
    </>
  );
}
